import type { MouseEventHandler, ReactNode } from 'react'
import { Link } from 'react-router'
import { Box } from '@mui/material'

import { tokens } from '@configuration/theme'
import Button from '@base/Button/Button.component'
import ArrowLeftIcon from '@icons/ArrowLeft.icon'

interface Props {
  /** Internal route (react-router Link). */
  to?: string
  /** External/return URL (anchor). */
  href?: string
  /** Click handler when neither `to` nor `href` is given. */
  onClick?: MouseEventHandler<HTMLElement>
  'aria-label'?: string
  dataTestId?: string
  children: ReactNode
}

// Top-of-card back affordance from the design: a small, muted ghost button
// with a leading arrow (login forgot/OTP "← Back to sign in"). Polymorphic so
// it works as a react-router Link (`to`), an anchor (`href`, for external
// return_to URLs), or a plain button (`onClick`).
const BackButton = ({
  to,
  href,
  onClick,
  'aria-label': ariaLabel,
  dataTestId = 'back-button',
  children,
}: Props) => {
  const sx = {
    alignSelf: 'flex-start',
    height: 32,
    minHeight: 32,
    padding: '0 10px 0 6px',
    fontSize: 13,
    fontWeight: 400,
    color: tokens.businessBlack60,
    '&:hover': { color: tokens.businessBlack },
  }
  const label = (
    <>
      <ArrowLeftIcon />
      <Box component='span'>{children}</Box>
    </>
  )

  if (to) {
    return (
      <Button
        variant='ghost'
        size='md'
        component={Link}
        to={to}
        sx={sx}
        aria-label={ariaLabel}
        dataTestId={dataTestId}
      >
        {label}
      </Button>
    )
  }
  if (href) {
    return (
      <Button
        variant='ghost'
        size='md'
        component='a'
        href={href}
        sx={sx}
        aria-label={ariaLabel}
        dataTestId={dataTestId}
      >
        {label}
      </Button>
    )
  }
  return (
    <Button
      variant='ghost'
      size='md'
      type='button'
      onClick={onClick}
      sx={sx}
      aria-label={ariaLabel}
      dataTestId={dataTestId}
    >
      {label}
    </Button>
  )
}

export default BackButton
